import React from "react";
import { Formik } from "formik";
import { Button, Form, Modal } from "react-bootstrap";
import * as Yup from "yup";
import { LoadingSpinner } from "../../common/Loading/LoadingSpinner";
import { IEvent } from "./EventCalendar";
import { FormField, FormAlert, DateTimeFields } from "../../Forms";
import { useEventUpdate } from "../../../hooks";

type Props = {
  show: boolean;
  onClose: (updated: boolean, removed: boolean) => void;
  eventToBeUpdated?: IEvent;
  identityIds?: string[];
};

const EventUpdate: React.FC<Props> = ({
  show,
  onClose,
  eventToBeUpdated,
  identityIds,
}) => {
  const {
    alertVariant,
    currentUser,
    allAppointmentTypes,
    allIdentities,
    handleSubmit,
    getInitialValues,
  } = useEventUpdate(eventToBeUpdated, identityIds);

  const handleClose = () => onClose(false, false);

  const onSubmit = async (values: any, formikHelpers: any) => {
    const success = await handleSubmit(values, formikHelpers);
    if (success) {
      onClose(true, false);
    }
  };

  return (
    <Modal centered show={show} keyboard={false} onHide={handleClose} size="lg">
      <Formik
        initialValues={getInitialValues()}
        validationSchema={Yup.object().shape({
          title: Yup.string().required("Title is required"),
          description: Yup.string().required("Description is required"),
          appointmentTypeId: Yup.string().required("Appointment Type is required"),
          selectedIdentityIds: Yup.array()
            .min(1, "Minimum of 1 user is required")
            .required("required"),
        })}
        onSubmit={onSubmit}
      >
        {({
          errors,
          status,
          touched,
          isSubmitting,
          handleSubmit,
          values,
          setFieldValue,
        }) => (
          <Form noValidate onSubmit={handleSubmit}>
            <Modal.Header closeButton>
              <Modal.Title>Update Event</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <FormField
                name="title"
                label="Event Name"
                required
                placeholder="Enter event name"
              />
              
              <FormField
                name="description"
                label="Event Description"
                required
                placeholder="Enter event description"
              />

              <FormField
                name="appointmentTypeId"
                showAppointmentType={true}
                allAppointmentTypes={allAppointmentTypes}
                eventToBeUpdated={eventToBeUpdated}
                setFieldValue={setFieldValue}
                required
              />

              <FormField
                name="selectedIdentityIds"
                showUserSelection={true}
                allIdentities={allIdentities}
                identityIds={identityIds}
                setFieldValue={setFieldValue}
                required
              />

              <FormField
                name="color"
                type="color"
                showColorPicker={true}
                currentUser={currentUser}
              />
              
              <DateTimeFields
                errors={errors}
                touched={touched}
                values={values}
                setFieldValue={setFieldValue}
              />

              {status && (
                <FormAlert message={status} variant={alertVariant as 'danger' | 'success' | 'warning' | 'info'} />
              )}
            </Modal.Body>
            <Modal.Footer>
              <div className="form-group">
                <Button
                  variant="primary"
                  disabled={isSubmitting}
                  type="submit"
                  className="me-2"
                >
                  {isSubmitting ? (
                    <LoadingSpinner text="Updating task..." />
                  ) : (
                    "Update Event"
                  )}
                </Button>
                <Button
                  variant="danger"
                  onClick={() => onClose(false, true)}
                  className="me-2"
                >
                  Remove
                </Button>
                <Button variant="secondary" onClick={handleClose}>
                  Cancel
                </Button>
              </div>
            </Modal.Footer>
          </Form>
        )}
      </Formik>
    </Modal>
  );
};

export { EventUpdate };
